/**
 * Brands A-Z Section JavaScript
 * Handles search functionality and alphabetical filtering
 */

class BrandsAZ {
  constructor() {
    this.searchInput = null;
    this.alphabetButtons = [];
    this.brandItems = [];
    this.letterGroups = [];
    this.currentFilter = null;
    this.searchTerm = '';
    this.brandsData = [];
    this.container = null;
    
    this.init();
  }

  init() {
    this.container = document.querySelector('[data-brands-container]');
    this.searchInput = document.querySelector('.brands-az-search-input');
    this.alphabetButtons = Array.from(document.querySelectorAll('.brands-az-alphabet-btn'));
    
    // Load brands data and render
    this.loadBrandsData();
    this.bindEvents();
    this.handleInitialState();
  }

  loadBrandsData() {
    const dataElement = document.querySelector('[data-brands-data]');
    if (!dataElement || !this.container) return;
    
    try {
      const data = JSON.parse(dataElement.textContent);
      this.brandsData = data.brands || [];
      
      // Process and display brands
      this.renderBrands();
      
      // Remove loading message
      const loadingEl = this.container.querySelector('.brands-az-loading');
      if (loadingEl) {
        loadingEl.remove();
      }
      
      // Update brand count if needed
      this.updateBrandCount();
    } catch (e) {
      console.error('Error loading brands data:', e);
    }
  }

  renderBrands() {
    if (!this.container) return;
    
    // Group brands by first letter
    const brandsByLetter = {};
    const alphabet = 'abcdefghijklmnopqrstuvwxyz';
    
    this.brandsData.forEach(brand => {
      if (!brand || brand.trim() === '') return;
      
      const brandLower = brand.toLowerCase().trim();
      const firstChar = brandLower.charAt(0);
      
      // Determine letter group
      let letter = '#';
      if (alphabet.includes(firstChar)) {
        letter = firstChar;
      }
      
      // Initialize letter group if needed
      if (!brandsByLetter[letter]) {
        brandsByLetter[letter] = [];
      }
      
      // Add brand if not already in array
      if (!brandsByLetter[letter].includes(brandLower)) {
        brandsByLetter[letter].push(brandLower);
      }
    });
    
    // Sort brands within each letter group
    Object.keys(brandsByLetter).forEach(letter => {
      brandsByLetter[letter].sort();
    });
    
    // Render letter groups
    const allLetters = ['#', ...alphabet.split('')];
    let html = '';
    
    allLetters.forEach(letter => {
      const brands = brandsByLetter[letter];
      if (brands && brands.length > 0) {
        html += `<div class="brands-az-letter-group" data-letter="${letter}">`;
        html += `<h2 class="brands-az-letter-header">${letter === '#' ? '#' : letter.toUpperCase()}</h2>`;
        html += '<div class="brands-az-brands-grid">';
        
        brands.forEach(brand => {
          const brandDisplay = this.capitalizeBrand(brand);
          const brandUrl = `/collections/all?filter.p.vendor=${encodeURIComponent(brand)}`;
          
          html += `<a href="${brandUrl}" class="brands-az-brand-item" data-brand="${brand}" data-brand-display="${brandDisplay}">${brandDisplay}</a>`;
        });
        
        html += '</div></div>';
      }
    });
    
    // Insert HTML
    const dataElement = document.querySelector('[data-brands-data]');
    if (dataElement) {
      dataElement.insertAdjacentHTML('afterend', html);
    } else {
      this.container.innerHTML = html;
    }
    
    // Update references
    this.brandItems = Array.from(document.querySelectorAll('.brands-az-brand-item'));
    this.letterGroups = Array.from(document.querySelectorAll('.brands-az-letter-group'));
  }

  capitalizeBrand(brand) {
    return brand.split(' ').map(word => {
      return word.charAt(0).toUpperCase() + word.slice(1).toLowerCase();
    }).join(' ');
  }

  updateBrandCount() {
    const countEl = document.querySelector('.brands-az-count');
    if (countEl && this.brandsData.length > 0) {
      countEl.textContent = this.brandsData.length;
    }
  }

  bindEvents() {
    // Search input
    if (this.searchInput) {
      this.searchInput.addEventListener('input', (e) => {
        this.handleSearch(e.target.value);
      });
      
      this.searchInput.addEventListener('keydown', (e) => {
        if (e.key === 'Escape') {
          this.clearSearch();
        }
      });
    }

    // Alphabet buttons
    this.alphabetButtons.forEach(button => {
      button.addEventListener('click', () => {
        const letter = button.dataset.letter;
        this.filterByLetter(letter);
        this.updateAlphabetButtons(button);
      });
    });
  }

  handleInitialState() {
    // Check if there's a search term in URL or hash
    const urlParams = new URLSearchParams(window.location.search);
    const searchParam = urlParams.get('search');
    if (searchParam) {
      this.searchInput.value = searchParam;
      this.handleSearch(searchParam);
    }

    // Check for letter filter in hash
    const hash = window.location.hash.substring(1);
    if (hash && hash.length === 1) {
      const letter = hash.toLowerCase();
      this.filterByLetter(letter);
      const button = this.alphabetButtons.find(btn => btn.dataset.letter === letter);
      if (button) {
        this.updateAlphabetButtons(button);
      }
    }
  }

  handleSearch(term) {
    this.searchTerm = term.toLowerCase().trim();
    this.currentFilter = null;
    
    // Clear alphabet button states
    this.alphabetButtons.forEach(btn => btn.classList.remove('active'));
    
    if (this.searchTerm === '') {
      this.showAll();
      return;
    }

    // Filter brands by search term
    let hasResults = false;
    
    this.letterGroups.forEach(group => {
      const brands = group.querySelectorAll('.brands-az-brand-item');
      let groupHasResults = false;
      
      brands.forEach(brand => {
        const brandName = brand.dataset.brand || brand.textContent.toLowerCase();
        const brandDisplay = brand.dataset.brandDisplay || brand.textContent.toLowerCase();
        
        if (brandName.includes(this.searchTerm) || brandDisplay.includes(this.searchTerm)) {
          brand.style.display = '';
          groupHasResults = true;
          hasResults = true;
        } else {
          brand.style.display = 'none';
        }
      });
      
      // Show/hide letter group based on results
      if (groupHasResults) {
        group.style.display = '';
      } else {
        group.style.display = 'none';
      }
    });

    // Scroll to first result
    if (hasResults) {
      const firstVisible = document.querySelector('.brands-az-brand-item[style=""]');
      if (firstVisible) {
        firstVisible.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
    }
  }

  filterByLetter(letter) {
    this.currentFilter = letter;
    this.searchTerm = '';
    
    // Clear search input
    if (this.searchInput) {
      this.searchInput.value = '';
    }
    
    // Update URL hash
    if (letter === '#') {
      window.history.replaceState(null, '', window.location.pathname + window.location.search);
    } else {
      window.history.replaceState(null, '', window.location.pathname + window.location.search + '#' + letter);
    }
    
    // Show/hide letter groups
    this.letterGroups.forEach(group => {
      const groupLetter = group.dataset.letter;
      if (groupLetter === letter) {
        group.style.display = '';
        // Scroll to group
        setTimeout(() => {
          group.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }, 100);
      } else {
        group.style.display = 'none';
      }
    });
  }

  updateAlphabetButtons(activeButton) {
    this.alphabetButtons.forEach(btn => {
      btn.classList.remove('active');
    });
    if (activeButton) {
      activeButton.classList.add('active');
    }
  }

  showAll() {
    this.currentFilter = null;
    this.letterGroups.forEach(group => {
      group.style.display = '';
      const brands = group.querySelectorAll('.brands-az-brand-item');
      brands.forEach(brand => {
        brand.style.display = '';
      });
    });
    
    // Clear URL hash
    window.history.replaceState(null, '', window.location.pathname + window.location.search);
  }

  clearSearch() {
    if (this.searchInput) {
      this.searchInput.value = '';
    }
    this.showAll();
    this.updateAlphabetButtons(null);
  }
}

// Initialize when DOM is ready
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => {
    new BrandsAZ();
  });
} else {
  new BrandsAZ();
}

// Handle section re-rendering in theme editor
if (typeof Shopify !== 'undefined' && Shopify.designMode) {
  document.addEventListener('shopify:section:load', (event) => {
    const section = document.querySelector(`[data-section-id="${event.detail.sectionId}"]`);
    if (section) {
      setTimeout(() => {
        new BrandsAZ();
      }, 100);
    }
  });
  
  document.addEventListener('shopify:section:reorder', () => {
    setTimeout(() => {
      new BrandsAZ();
    }, 100);
  });
}

